import React, { useState, useEffect } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { getAllEpochs, getUserClaims } from "../utils/indexerClient";

interface HistoricalEpoch {
  epochIndex: number;
  startTime: number;
  endTime: number;
  epochEp: string;
  totalStakeSeconds: string;
  rewardsMerkleRoot: string | null;
  status: string;
  createdAt: string;
  finalizedAt: string | null;
}

export const EpochHistory: React.FC = () => {
  const { publicKey } = useWallet();
  const [epochs, setEpochs] = useState<HistoricalEpoch[]>([]);
  const [userClaims, setUserClaims] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    fetchEpochHistory();
  }, [publicKey]);

  const fetchEpochHistory = async () => {
    try {
      setLoading(true);
      const allEpochs = await getAllEpochs();

      // Sort by epoch index descending (newest first)
      const sortedEpochs = allEpochs.sort(
        (a, b) => b.epochIndex - a.epochIndex
      );
      setEpochs(sortedEpochs);

      // Fetch user's claim history if wallet connected
      if (publicKey) {
        const claims = await getUserClaims(publicKey.toBase58());
        const claimedSet = new Set(claims.map((c: any) => c.epoch_index));
        setUserClaims(claimedSet);
      }
    } catch (err: any) {
      console.error("Error fetching epoch history:", err);
      setError("Failed to load epoch history");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getEpochStatus = (epoch: HistoricalEpoch) => {
    const now = Math.floor(Date.now() / 1000);
    if (now < epoch.startTime)
      return { label: "Upcoming", color: "bg-blue-100 text-blue-800" };
    if (now >= epoch.startTime && now < epoch.endTime)
      return { label: "Active", color: "bg-green-100 text-green-800" };
    if (epoch.rewardsMerkleRoot)
      return { label: "Finalized", color: "bg-purple-100 text-purple-800" };
    return { label: "Ended", color: "bg-gray-100 text-gray-800" };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-habitat-primary mx-auto mb-4"></div>
          <p className="text-habitat-neutral-600">Loading epoch history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center">
        <p className="text-red-600">{error}</p>
        <button onClick={fetchEpochHistory} className="btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-habitat-neutral-900">
          Epoch History
        </h2>
        <button onClick={fetchEpochHistory} className="btn-outline text-sm">
          Refresh
        </button>
      </div>

      {epochs.length === 0 ? (
        <div className="card text-center">
          <p className="text-habitat-neutral-600">No epochs found yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {epochs.map((epoch) => {
            const status = getEpochStatus(epoch);
            const hasClaimed = userClaims.has(epoch.epochIndex);

            return (
              <div
                key={epoch.epochIndex}
                className="card hover:shadow-habitat-md transition-shadow duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-habitat-primary">
                      Epoch #{epoch.epochIndex}
                    </h3>
                    <p className="text-sm text-habitat-neutral-500 mt-1">
                      {formatDate(epoch.startTime)} -{" "}
                      {formatDate(epoch.endTime)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}
                    >
                      {status.label}
                    </span>
                    {publicKey && hasClaimed && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        Claimed
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-habitat-neutral-500 mb-1">Reward Pool</p>
                    <p className="font-semibold text-habitat-secondary">
                      {Number(epoch.epochEp).toLocaleString()} EP
                    </p>
                  </div>
                  <div>
                    <p className="text-habitat-neutral-500 mb-1">
                      Total Stake-Seconds
                    </p>
                    <p className="font-semibold text-habitat-neutral-900">
                      {epoch.totalStakeSeconds
                        ? Number(epoch.totalStakeSeconds).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-habitat-neutral-500 mb-1">Finalized</p>
                    <p className="font-semibold text-habitat-neutral-900">
                      {epoch.finalizedAt
                        ? formatDate(
                            new Date(epoch.finalizedAt).getTime() / 1000
                          )
                        : "Not yet"}
                    </p>
                  </div>
                </div>

                {epoch.rewardsMerkleRoot && (
                  <div className="mt-4 pt-4 border-t border-habitat-neutral-200">
                    <p className="text-xs text-habitat-neutral-500 mb-1">
                      Merkle Root
                    </p>
                    <p className="text-xs font-mono text-habitat-neutral-700 break-all">
                      {epoch.rewardsMerkleRoot}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Info Section */}
      <div className="card bg-habitat-neutral-50">
        <h3 className="font-semibold text-habitat-neutral-900 mb-3">
          About Epochs
        </h3>
        <ul className="space-y-2 text-sm text-habitat-neutral-600">
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>
              Epochs are time periods during which staking rewards are
              accumulated
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>
              Rewards are distributed based on your stake-seconds (amount × time
              staked)
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>
              Each epoch must be finalized before rewards can be claimed
            </span>
          </li>
          <li className="flex items-start">
            <span className="text-habitat-primary mr-2">•</span>
            <span>
              Your portion of rewards = (your stake-seconds / total
              stake-seconds) × epoch pool
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};
